import React, { useState, useEffect } from 'react';
import {
  Content,
  Header,
  Page,
  InfoCard,
} from '@backstage/core-components';
import {
  Button,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  Stepper,
  Step,
  StepLabel,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Link,
  Chip,
  Divider,
  IconButton,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  CheckCircle,
  Error as ErrorIcon,
  Settings,
  Code,
  Add,
  Launch,
  Storage,
  Timeline,
  Delete as DeleteIcon,
  Group,
  Email,
  Send,
  HourglassEmpty,
  MailOutline,
  CheckCircleOutline,
  ErrorOutline,
} from '@material-ui/icons';
import { Alert } from '@material-ui/lab';
import { useApi, configApiRef } from '@backstage/core-plugin-api';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  formSection: {
    marginBottom: theme.spacing(3),
  },
  stepContent: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  buttonGroup: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(2),
    marginTop: theme.spacing(3),
  },
  resultCard: {
    marginTop: theme.spacing(2),
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '200px',
  },
  successIcon: {
    color: theme.palette.success.main,
  },
  errorIcon: {
    color: theme.palette.error.main,
  },
  reviewGrid: {
    marginTop: theme.spacing(2),
  },
  urlBox: {
    backgroundColor: theme.palette.grey[100],
    padding: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    fontFamily: 'monospace',
    fontSize: '0.875rem',
    wordBreak: 'break-all',
    marginTop: theme.spacing(1),
  },
  successDetails: {
    marginTop: theme.spacing(2),
  },
  linkButton: {
    marginTop: theme.spacing(1),
    marginRight: theme.spacing(1),
  },
  featureChip: {
    margin: theme.spacing(0.5),
  },
  infoSection: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
  },
  debugSection: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.grey[300]}`,
  },
  debugText: {
    fontFamily: 'monospace',
    fontSize: '0.75rem',
    whiteSpace: 'pre-wrap',
    maxHeight: '200px',
    overflow: 'auto',
  },
  emailInputContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  emailChip: {
    margin: theme.spacing(0.25),
  },
  teamMembersSection: {
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  requestorSection: {
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  testConnectionSection: {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.grey[300]}`,
  },
  disabledSection: {
    opacity: 0.6,
    pointerEvents: 'none',
  },
  pendingApprovalSection: {
    textAlign: 'center',
    padding: theme.spacing(4),
  },
  approvalPendingIcon: {
    fontSize: '4rem',
    color: theme.palette.warning.main,
    marginBottom: theme.spacing(2),
  },
  emailStatusCard: {
    background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
    border: `1px solid ${theme.palette.primary.light}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    margin: theme.spacing(2, 0),
  },
  emailSuccessCard: {
    background: 'linear-gradient(135deg, #e8f5e8 0%, #c8e6c9 100%)',
    border: `1px solid ${theme.palette.success.light}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    margin: theme.spacing(2, 0),
  },
  emailErrorCard: {
    background: 'linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)',
    border: `1px solid ${theme.palette.error.light}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    margin: theme.spacing(2, 0),
  },
  statusBadge: {
    padding: theme.spacing(0.5, 1),
    borderRadius: theme.spacing(1),
    fontSize: '0.75rem',
    fontWeight: 'bold',
    textTransform: 'uppercase',
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  statusPending: {
    backgroundColor: '#fff3cd',
    color: '#856404',
    border: '1px solid #ffeaa7',
  },
  statusApproved: {
    backgroundColor: '#d4edda',
    color: '#155724',
    border: '1px solid #c3e6cb',
  },
  statusRejected: {
    backgroundColor: '#f8d7da',
    color: '#721c24',
    border: '1px solid #f5c6cb',
  },
}));

interface ProjectData {
  organizationName: string;
  projectName: string;
  projectDescription: string;
  isDotNetProject: boolean;
  teamMembers: string[];
  requestorEmails: string[];
}

interface CreateProjectResponse {
  success: boolean;
  projectId?: string;
  repositoryId?: string;
  message: string;
  projectUrl?: string;
  repositoryUrl?: string;
  addedMembers?: string[];
  failedMembers?: string[];
  branchesCreated?: string[];
  policiesApplied?: boolean;
}

interface ApprovalResponse {
  success: boolean;
  message: string;
  requestId?: string;
  approvalEmailSent?: boolean;
}

interface ApprovalStatusResponse {
  success: boolean;
  requestId: string;
  status: 'pending' | 'approved' | 'rejected';
  projectName: string;
  requestorEmails: string[];
  submittedAt: string;
  processedAt?: string;
  statusInfo?: {
    approved: boolean;
    message: string;
    projectUrl?: string;
    processedAt: string;
  };
  message: string;
}

export const SwbcAdoProjectPluginPage = () => {
  const classes = useStyles();
  const config = useApi(configApiRef);
  const [activeStep, setActiveStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [creationResult, setCreationResult] = useState<CreateProjectResponse | null>(null);
  const [approvalResult, setApprovalResult] = useState<ApprovalResponse | null>(null);
  const [approvalStatus, setApprovalStatus] = useState<ApprovalStatusResponse | null>(null);
  const [debugInfo, setDebugInfo] = useState<string>('');
  const [newMemberEmail, setNewMemberEmail] = useState<string>('');
  const [newRequestorEmail, setNewRequestorEmail] = useState<string>('');
  const [isCheckingStatus, setIsCheckingStatus] = useState(false);
  const [emailTestResult, setEmailTestResult] = useState<any>(null);
  const [formData, setFormData] = useState<ProjectData>({
    organizationName: 'SWBC-FigWebDev', 
    projectName: '',
    projectDescription: '',
    isDotNetProject: false,
    teamMembers: [],
    requestorEmails: [],
  });

  const steps = ['Project Details', 'Review & Submit for Approval', 'Approval Status'];

  const addDebugInfo = (info: string) => {
    const timestamp = new Date().toLocaleTimeString();
    setDebugInfo(prev => `${prev}[${timestamp}] ${info}\n`);
    console.log(`[DEBUG] ${info}`);
  };

  const handleInputChange = (field: keyof ProjectData, value: string | boolean | string[]) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const addRequestorEmail = () => {
    if (newRequestorEmail.trim() && !formData.requestorEmails.includes(newRequestorEmail.trim())) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (emailRegex.test(newRequestorEmail.trim())) {
        handleInputChange('requestorEmails', [...formData.requestorEmails, newRequestorEmail.trim()]);
        setNewRequestorEmail('');
      } else {
        alert('Please enter a valid email address');
      }
    }
  };

  const removeRequestorEmail = (email: string) => {
    handleInputChange('requestorEmails', formData.requestorEmails.filter(requestor => requestor !== email));
  };

  const addTeamMember = () => {
    if (newMemberEmail.trim() && !formData.teamMembers.includes(newMemberEmail.trim())) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (emailRegex.test(newMemberEmail.trim())) {
        handleInputChange('teamMembers', [...formData.teamMembers, newMemberEmail.trim()]);
        setNewMemberEmail('');
      } else {
        alert('Please enter a valid email address');
      }
    }
  };

  const removeTeamMember = (email: string) => {
    handleInputChange('teamMembers', formData.teamMembers.filter(member => member !== email));
  };

  const handleKeyPress = (event: React.KeyboardEvent, type: 'team' | 'requestor') => {
    if (event.key === 'Enter') {
      event.preventDefault();
      if (type === 'team') {
        addTeamMember();
      } else {
        addRequestorEmail();
      }
    }
  };

  // Function to check approval status
  const checkApprovalStatus = async (requestId: string) => {
    try {
      setIsCheckingStatus(true);
      addDebugInfo(`Checking approval status for request: ${requestId}`);
      
      const baseUrl = config.getString('backend.baseUrl') || '';

      const response = await fetch(`${baseUrl}/api/ado-project/status/${requestId}`);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const statusData: ApprovalStatusResponse = await response.json();
      addDebugInfo(`Status check result: ${JSON.stringify(statusData, null, 2)}`);
      
      setApprovalStatus(statusData);
      
      return statusData;
    } catch (error) {
      addDebugInfo(`Status check failed: ${error}`);
      console.error('Status check error:', error);
      return null;
    } finally {
      setIsCheckingStatus(false);
    }
  };

  // Auto-check status when we have a request ID
  useEffect(() => {
    if (approvalResult?.requestId && approvalResult.success) {
      // Initial check after 5 seconds
      const initialTimer = setTimeout(() => {
        checkApprovalStatus(approvalResult.requestId!);
      }, 5000);

      // Then check every 30 seconds
      const interval = setInterval(() => {
        checkApprovalStatus(approvalResult.requestId!);
      }, 30000);

      return () => {
        clearTimeout(initialTimer);
        clearInterval(interval);
      };
    }
  }, [approvalResult?.requestId]);

  const testEmailConnection = async () => {
    try {
      addDebugInfo('Testing email configuration...');
      setEmailTestResult(null);

      
      // const baseUrl = config.getOptionalString('app_plugins.baseUrl') || '';
      const baseUrl = config.getOptionalString('backend.baseUrl') || '';

      const response = await fetch(`${baseUrl}/api/ado-project/test-email`);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }
      
      const result = await response.json();
      addDebugInfo(`Email test result: ${JSON.stringify(result)}`);
      setEmailTestResult(result);
      
    } catch (error) {
      addDebugInfo(`Email test failed: ${error}`);
      setEmailTestResult({
        success: false,
        message: `Email test failed: ${error}`,
        configured: false
      });
    }
  };

  const testConnection = async () => {
    try {
      addDebugInfo('Testing backend connection...');

      // const baseUrl = config.getOptionalString('app_plugins.baseUrl') || '';
      const baseUrl = config.getOptionalString('backend.baseUrl') || '';

      const response = await fetch(`${baseUrl}/api/ado-project/health`);

      addDebugInfo(`Health check response status: ${response.status}`);
      addDebugInfo(`Health check response headers: ${JSON.stringify(Array.from(response.headers.entries()))}`);
      
      if (!response.ok) {
        const errorText = await response.text();
        addDebugInfo(`Health check failed with status ${response.status}: ${errorText}`);
        return;
      }
      
      const result = await response.json();
      addDebugInfo(`Health check result: ${JSON.stringify(result)}`);
      
      // Testing the actual endpoint
      addDebugInfo('Testing /test endpoint...');

      const testResponse = await fetch(`${baseUrl}/api/ado-project/test`);
      addDebugInfo(`Test response status: ${testResponse.status}`);
      
      if (!testResponse.ok) {
        const testErrorText = await testResponse.text();
        addDebugInfo(`Test endpoint failed with status ${testResponse.status}: ${testErrorText}`);
        return;
      }
      
      const testResult = await testResponse.json();
      addDebugInfo(`Test endpoint result: ${JSON.stringify(testResult)}`);
      
    } catch (error) {
      addDebugInfo(`Connection test failed: ${error}`);
    }
  };

  const renderProjectDetailsStep = () => (
    <Box className={classes.stepContent}>
      <Box className={classes.infoSection}>
        <Typography variant="body2" color="textSecondary">
          Create a new Azure DevOps project with an initial repository. This wizard will help you set up 
          a complete project structure with Git version control, private visibility, 
          three branches (master, integration, release) with security policies, and optional .NET starter files.
          <br /><br />
          <strong>Note:</strong> Your request will be sent for approval via email before the project is created.
        </Typography>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Organization Name"
            value={formData.organizationName}
            onChange={(e) => handleInputChange('organizationName', e.target.value)}
            placeholder="SWBC-FigWebDev"
            required
            variant="outlined"
            helperText="The Azure DevOps organization where the project will be created"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Project Name"
            value={formData.projectName}
            onChange={(e) => handleInputChange('projectName', e.target.value)}
            placeholder="my-awesome-project"
            required
            variant="outlined"
            helperText="Must be unique within your organization"
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Project Description"
            value={formData.projectDescription}
            onChange={(e) => handleInputChange('projectDescription', e.target.value)}
            placeholder="Describe your project..."
            required
            multiline
            rows={3}
            variant="outlined"
            helperText="Provide a clear description of what this project will contain"
          />
        </Grid>

        {/* Fixed Configuration Section */}
        <Grid item xs={12}>
          <Alert severity="info" style={{ marginBottom: 16 }}>
            <Typography variant="body2">
              <strong>Project Configuration:</strong> All projects will be created with Git version control and private visibility. 
              The repository will include master, integration, and release branches with appropriate security policies.
            </Typography>
          </Alert>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl component="fieldset" className={`${classes.formSection} ${classes.disabledSection}`}>
            <FormLabel component="legend">Version Control System (Fixed)</FormLabel>
            <RadioGroup value="git">
              <FormControlLabel 
                value="git" 
                control={<Radio checked />} 
                label={
                  <Box display="flex" alignItems="center">
                    <Storage style={{ marginRight: 8 }} />
                    Git (Required)
                  </Box>
                } 
                disabled
              />
            </RadioGroup>
          </FormControl>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <FormControl component="fieldset" className={`${classes.formSection} ${classes.disabledSection}`}>
            <FormLabel component="legend">Repository Visibility (Fixed)</FormLabel>
            <RadioGroup value="private">
              <FormControlLabel 
                value="private" 
                control={<Radio checked />} 
                label="Private (Required for security)" 
                disabled
              />
            </RadioGroup>
          </FormControl>
        </Grid>

        {/* Team Members Section */}
        <Grid item xs={12}>
          <Box className={classes.teamMembersSection}>
            <Box display="flex" alignItems="center" mb={2}>
              <Group style={{ marginRight: 8 }} />
              <Typography variant="h6" component="h3">
                Team Members
              </Typography>
            </Box>
            
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Add team members by email address. They will be added to the default project team.
            </Typography>
            
            <Box className={classes.emailInputContainer}>
              <TextField
                fullWidth
                label="Team Member Email"
                value={newMemberEmail}
                onChange={(e) => setNewMemberEmail(e.target.value)}
                onKeyPress={(e) => handleKeyPress(e, 'team')}
                placeholder="user@company.com"
                variant="outlined"
                size="small"
                InputProps={{
                  startAdornment: <Email style={{ marginRight: 8, color: 'rgba(0, 0, 0, 0.54)' }} />,
                }}
              />
              <Button
                variant="outlined"
                onClick={addTeamMember}
                disabled={!newMemberEmail.trim()}
                startIcon={<Add />}
              >
                Add
              </Button>
            </Box>

            {formData.teamMembers.length > 0 && (
              <Box mt={2}>
                <Typography variant="body2" gutterBottom>
                  <strong>Team Members ({formData.teamMembers.length}):</strong>
                </Typography>
                <Box>
                  {formData.teamMembers.map((email, index) => (
                    <Chip
                      key={index}
                      label={email}
                      onDelete={() => removeTeamMember(email)}
                      deleteIcon={<DeleteIcon />}
                      className={classes.emailChip}
                      color="primary"
                      variant="outlined"
                    />
                  ))}
                </Box>
                <Box mt={1}>
                  <Button
                    size="small"
                    onClick={() => handleInputChange('teamMembers', [])}
                    color="secondary"
                  >
                    Clear All Members
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Divider style={{ margin: '16px 0' }} />
          <Box display="flex" alignItems="center" className={classes.formSection}>
            <Code style={{ marginRight: 8 }} />
            <Typography variant="h6" component="h3">
              Project Template
            </Typography>
          </Box>
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.isDotNetProject}
                onChange={(e) => handleInputChange('isDotNetProject', e.target.checked)}
              />
            }
            label={
              <Box>
                <Typography variant="body1">.NET Project Template</Typography>
                <Typography variant="body2" color="textSecondary">
                  Includes starter files: Program.cs, .csproj, README.md, and .gitignore
                </Typography>
              </Box>
            }
          />
          {formData.isDotNetProject && (
            <Box mt={2}>
              <Typography variant="body2" color="primary">
                <strong>Included files:</strong>
              </Typography>
              <Box mt={1}>
                <Chip label="Program.cs" size="small" className={classes.featureChip} />
                <Chip label=".csproj" size="small" className={classes.featureChip} />
                <Chip label="README.md" size="small" className={classes.featureChip} />
                <Chip label=".gitignore" size="small" className={classes.featureChip} />
              </Box>
            </Box>
          )}
        </Grid>
      </Grid>
    </Box>
  );

  const renderReviewStep = () => (
    <Box className={classes.stepContent}>
      <Alert severity="warning" icon={<Send />} style={{ marginBottom: 16 }}>
        Review your project configuration and specify who is requesting this project before submitting for approval via email.
      </Alert>

      {/* Who is requesting this? Section */}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Box className={classes.requestorSection}>
            <Box display="flex" alignItems="center" mb={2}>
              <Email style={{ marginRight: 8 }} />
              <Typography variant="h6" component="h3">
                Who is requesting this?
              </Typography>
            </Box>
            
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Add email addresses of people who are requesting this project. They will be notified via email about the approval status.
            </Typography>
            
            <Box className={classes.emailInputContainer}>
              <TextField
                fullWidth
                label="Requestor Email"
                value={newRequestorEmail}
                onChange={(e) => setNewRequestorEmail(e.target.value)}
                onKeyPress={(e) => handleKeyPress(e, 'requestor')}
                placeholder="requestor@company.com"
                variant="outlined"
                size="small"
                InputProps={{
                  startAdornment: <Email style={{ marginRight: 8, color: 'rgba(0, 0, 0, 0.54)' }} />,
                }}
              />
              <Button
                variant="outlined"
                onClick={addRequestorEmail}
                disabled={!newRequestorEmail.trim()}
                startIcon={<Add />}
              >
                Add
              </Button>
            </Box>

            {formData.requestorEmails.length > 0 && (
              <Box mt={2}>
                <Typography variant="body2" gutterBottom>
                  <strong>Requestors ({formData.requestorEmails.length}):</strong>
                </Typography>
                <Box>
                  {formData.requestorEmails.map((email, index) => (
                    <Chip
                      key={index}
                      label={email}
                      onDelete={() => removeRequestorEmail(email)}
                      deleteIcon={<DeleteIcon />}
                      className={classes.emailChip}
                      color="secondary"
                      variant="outlined"
                    />
                  ))}
                </Box>
                <Box mt={1}>
                  <Button
                    size="small"
                    onClick={() => handleInputChange('requestorEmails', [])}
                    color="secondary"
                  >
                    Clear All Requestors
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>
      
      <Grid container spacing={3} className={classes.reviewGrid}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="primary" gutterBottom>
                Project Configuration
              </Typography>
              <Box>
                <Typography><strong>Organization:</strong> {formData.organizationName}</Typography>
                <Typography><strong>Project Name:</strong> {formData.projectName}</Typography>
                <Typography><strong>Description:</strong> {formData.projectDescription}</Typography>
                <Typography><strong>Version Control:</strong> Git (Fixed)</Typography>
                <Typography><strong>Visibility:</strong> Private (Fixed)</Typography>
                <Typography><strong>Template:</strong> {formData.isDotNetProject ? '.NET Project' : 'Empty Project'}</Typography>
                <Typography><strong>Team Members:</strong> {formData.teamMembers.length}</Typography>
                <Typography><strong>Requestors:</strong> {formData.requestorEmails.length}</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="primary" gutterBottom>
                What will be created after approval:
              </Typography>
              <Box>
                <Typography>• Azure DevOps Project: <strong>{formData.projectName}</strong></Typography>
                <Typography>• Initial Repository: <strong>{formData.projectName}</strong></Typography>
                <Typography>• Version Control: <strong>Git</strong></Typography>
                <Typography>• Visibility: <strong>Private</strong></Typography>
                <Typography>• Branches: <strong>master, integration, release</strong></Typography>
                <Typography>• Security policies on release branch</Typography>
                {formData.teamMembers.length > 0 && (
                  <Typography>• {formData.teamMembers.length} members added to default team</Typography>
                )}
                {formData.isDotNetProject && (
                  <>
                    <Typography>• .NET 8.0 project structure</Typography>
                    <Typography>• Starter application code</Typography>
                    <Typography>• Development guidelines</Typography>
                    <Typography>• Git ignore file</Typography>
                  </>
                )}
              </Box>
              
              {formData.isDotNetProject && (
                <Box mt={2}>
                  <Alert severity="success" style={{ fontSize: '0.875rem' }}>
                    Your project will be ready for immediate development with a working .NET application after approval!
                  </Alert>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Team Members Review */}
        {formData.teamMembers.length > 0 && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="primary" gutterBottom>
                  <Group style={{ marginRight: 8, verticalAlign: 'middle' }} />
                  Team Members to be Added
                </Typography>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  These members will be added to the default project team:
                </Typography>
                <Box mt={1}>
                  {formData.teamMembers.map((email, index) => (
                    <Chip
                      key={index}
                      label={email}
                      className={classes.emailChip}
                      color="primary"
                      variant="outlined"
                      size="small"
                    />
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Requestors Review */}
        {formData.requestorEmails.length > 0 && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="primary" gutterBottom>
                  <Email style={{ marginRight: 8, verticalAlign: 'middle' }} />
                  Project Requestors
                </Typography>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  These people are requesting this project and will be notified via email about the approval status:
                </Typography>
                <Box mt={1}>
                  {formData.requestorEmails.map((email, index) => (
                    <Chip
                      key={index}
                      label={email}
                      className={classes.emailChip}
                      color="secondary"
                      variant="outlined"
                      size="small"
                    />
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
      
      <Box mt={3}>
        <Typography variant="h6" gutterBottom>📧 Email Approval Process:</Typography>
        <Box className={classes.urlBox}>
          <Typography variant="body2">
            <strong>1.</strong> Your request will be submitted and stored in the system
          </Typography>
          <Typography variant="body2">
            <strong>2.</strong> Administrator will receive a detailed email with approve/reject buttons
          </Typography>
          <Typography variant="body2">
            <strong>3.</strong> Status updates will appear directly in this plugin
          </Typography>
          <Typography variant="body2">
            <strong>4.</strong> Requestors will receive email notifications when approved/rejected
          </Typography>
          <Typography variant="body2">
            <strong>5.</strong> If approved, the project will be created automatically
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  const renderApprovalStatusStep = () => (
    <Box className={classes.stepContent}>
      {isLoading ? (
        <Box className={classes.loadingContainer}>
          <CircularProgress size={64} />
          <Typography variant="h6" style={{ marginTop: 16 }}>
            Submitting your request for approval...
          </Typography>
          <Typography color="textSecondary" style={{ marginTop: 8 }}>
            Please wait while we send the approval email to the administrator.
          </Typography>
          
          {/* Debug information during loading */}
          {debugInfo && (
            <Box className={classes.debugSection} style={{ marginTop: 16, width: '100%' }}>
              <Typography variant="body2" gutterBottom>
                <strong>Debug Information:</strong>
              </Typography>
              <Box className={classes.debugText}>
                {debugInfo}
              </Box>
            </Box>
          )}
        </Box>
      ) : approvalResult ? (
        <Box>
          {/* Email Status Card */}
          {approvalResult.success ? (
            <Box className={classes.emailSuccessCard}>
              <Box display="flex" alignItems="center" mb={1}>
                <CheckCircleOutline style={{ color: '#2e7d32', marginRight: 8 }} />
                <Typography variant="h6" style={{ color: '#2e7d32' }}>
                  📧 Email Sent Successfully!
                </Typography>
              </Box>
              <Typography variant="body2" style={{ color: '#2e7d32' }}>
                The approval request email has been sent to the administrator with interactive approve/reject buttons.
              </Typography>
            </Box>
          ) : (
            <Box className={classes.emailErrorCard}>
              <Box display="flex" alignItems="center" mb={1}>
                <ErrorOutline style={{ color: '#d32f2f', marginRight: 8 }} />
                <Typography variant="h6" style={{ color: '#d32f2f' }}>
                  📧 Email Failed to Send
                </Typography>
              </Box>
              <Typography variant="body2" style={{ color: '#d32f2f' }}>
                There was an issue sending the approval email. Please check the email configuration.
              </Typography>
            </Box>
          )}

          <Alert 
            severity={approvalResult.success ? "success" : "error"}
            icon={approvalResult.success ? <Send /> : <ErrorIcon />}
            className={classes.resultCard}
          >
            {approvalResult.message}
          </Alert>
          
          {approvalResult.success && (
            <Box className={classes.pendingApprovalSection}>
              <HourglassEmpty className={classes.approvalPendingIcon} />
              <Typography variant="h5" gutterBottom>
                Request Submitted Successfully!
              </Typography>
              
              <Typography variant="body1" color="textSecondary" paragraph>
                Your project creation request has been sent for approval via email.
              </Typography>
              
              {/* Real-time status display */}
              {approvalStatus && (
                <Card className={classes.resultCard} style={{ marginBottom: 16 }}>
                  <CardContent>
                    <Box display="flex" alignItems="center" justifyContent="center" mb={2}>
                      <Typography variant="h6" style={{ marginRight: 8 }}>
                        📊 Current Status:
                      </Typography>
                      <span className={`${classes.statusBadge} ${
                        approvalStatus.status === 'pending' ? classes.statusPending :
                        approvalStatus.status === 'approved' ? classes.statusApproved :
                        classes.statusRejected
                      }`}>
                        {approvalStatus.status === 'pending' && <HourglassEmpty fontSize="small" />}
                        {approvalStatus.status === 'approved' && <CheckCircle fontSize="small" />}
                        {approvalStatus.status === 'rejected' && <ErrorIcon fontSize="small" />}
                        {approvalStatus.status.toUpperCase()}
                      </span>
                    </Box>
                    
                    {approvalStatus.status === 'pending' && (
                      <Alert severity="info" style={{ marginBottom: 16 }}>
                        <Typography variant="body2">
                          ⏳ Your request is being reviewed by the administrator.
                          {isCheckingStatus && ' (Checking for updates...)'}
                        </Typography>
                      </Alert>
                    )}
                    
                    {approvalStatus.status === 'approved' && (
                      <Alert severity={approvalStatus.statusInfo?.message?.includes('failed') ? "error" : "success"} style={{ marginBottom: 16 }}>
                        <Typography variant="body2">
                          {approvalStatus.statusInfo?.message?.includes('failed') ? 
                            '❌ Project creation failed after approval.' : 
                            '✅ Your request has been approved and the project has been created!'}
                        </Typography>
                        {approvalStatus.statusInfo?.message && (
                          <Typography variant="body2" style={{ marginTop: 8 }} dangerouslySetInnerHTML={{ __html: `<strong>Details:</strong> ${approvalStatus.statusInfo.message}` }} />
                        )}
                        {approvalStatus.statusInfo?.projectUrl && !approvalStatus.statusInfo?.message?.includes('failed') && (
                          <Box mt={2}>
                            <Button
                              variant="contained"
                              color="primary"
                              startIcon={<Launch />}
                              href={approvalStatus.statusInfo.projectUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              🚀 Open Project
                            </Button>
                          </Box>
                        )}
                      </Alert>
                    )}
                    
                    {approvalStatus.status === 'rejected' && (
                      <Alert severity="error" style={{ marginBottom: 16 }}>
                        <Typography variant="body2">
                          ❌ Your request has been rejected.
                        </Typography>
                        {approvalStatus.statusInfo?.message && (
                          <Typography variant="body2" style={{ marginTop: 8 }} dangerouslySetInnerHTML={{ __html: `<strong>Reason:</strong> ${approvalStatus.statusInfo.message}` }} />
                        )}
                      </Alert>
                    )}
                    
                    <Typography variant="body2" color="textSecondary">
                      <strong>Submitted:</strong> {new Date(approvalStatus.submittedAt).toLocaleString()}
                    </Typography>
                    
                    {approvalStatus.processedAt && (
                      <Typography variant="body2" color="textSecondary">
                        <strong>Processed:</strong> {new Date(approvalStatus.processedAt).toLocaleString()}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              )}
              
              <Card className={classes.resultCard}>
                <CardContent>
                  <Typography variant="h6" color="primary" gutterBottom>
                    📧 What happens next?
                  </Typography>
                  
                  <Grid container spacing={2} className={classes.successDetails}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle1" gutterBottom>
                        <strong>📧 Email Approval Process:</strong>
                      </Typography>
                      <Typography variant="body2">✅ Approval email sent to administrator</Typography>
                      <Typography variant="body2">📧 Administrator will receive interactive email with approve/reject buttons</Typography>
                      <Typography variant="body2">⏳ Status updates will appear here automatically</Typography>
                      <Typography variant="body2">🔄 This page checks for updates every 30 seconds</Typography>
                      <Typography variant="body2">📬 Requestors will receive email notifications when processed</Typography>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle1" gutterBottom>
                        <strong>Request Details:</strong>
                      </Typography>
                      {approvalResult.requestId && (
                        <Typography variant="body2">Request ID: {approvalResult.requestId}</Typography>
                      )}
                      <Typography variant="body2">Project: {formData.projectName}</Typography>
                      <Typography variant="body2">Requestors: {formData.requestorEmails.length > 0 ? formData.requestorEmails.join(', ') : 'None specified'}</Typography>
                      <Typography variant="body2">Submitted: {new Date().toLocaleString()}</Typography>
                      
                      {approvalResult.requestId && (
                        <Box mt={2}>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => checkApprovalStatus(approvalResult.requestId!)}
                            disabled={isCheckingStatus}
                            startIcon={isCheckingStatus ? <CircularProgress size={16} /> : <Timeline />}
                          >
                            {isCheckingStatus ? 'Checking...' : 'Check Status Now'}
                          </Button>
                        </Box>
                      )}
                    </Grid>
                  </Grid>
                  
                  <Divider style={{ margin: '16px 0' }} />
                  
                  <Alert severity="info" style={{ marginTop: 16 }}>
                    <Typography variant="body2">
                      <strong>📧 Email notifications:</strong> Both administrator and requestors will receive email notifications<br/>
                      <strong>🔄 Real-time updates:</strong> Status changes will appear here automatically<br/>
                      <strong>❓ Questions?</strong> Contact FIG DevOps
                    </Typography>
                  </Alert>
                </CardContent>
              </Card>
            </Box>
          )}
          
          {!approvalResult.success && (
            <Card className={classes.resultCard}>
              <CardContent>
                <Typography variant="h6" color="error" gutterBottom>
                  ⚠️ Request Submission Failed
                </Typography>
                
                <Typography variant="body2" style={{ marginBottom: 16 }}>
                  <strong>What happened:</strong> {approvalResult.message}
                </Typography>
                
                <Typography variant="body2" style={{ marginBottom: 16 }}>
                  <strong>Next steps:</strong>
                </Typography>
                
                <Typography variant="body2" component="div" style={{ marginLeft: 16, marginBottom: 16 }}>
                  1. Check your email configuration in app-config.yaml<br/>
                  2. Verify SMTP settings are correct<br/>
                  3. Test email connection using the button below<br/>
                  4. Try submitting the request again<br/>
                  5. Contact IT support if the problem persists
                </Typography>
                
                <Box mt={2}>
                  <Button
                    variant="outlined"
                    onClick={testEmailConnection}
                    startIcon={<MailOutline />}
                    style={{ marginRight: 8 }}
                  >
                    Test Email Configuration
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleStartOver}
                    startIcon={<Add />}
                  >
                    Try Again
                  </Button>
                </Box>
              </CardContent>
            </Card>
          )}
        </Box>
      ) : null}
    </Box>
  );

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return renderProjectDetailsStep();
      case 1:
        return renderReviewStep();
      case 2:
        return renderApprovalStatusStep();
      default:
        return null;
    }
  };

  const isStepValid = (step: number): boolean => {
    switch (step) {
      case 0:
        return !!(formData.organizationName && formData.projectName && formData.projectDescription);
      case 1:
        return formData.requestorEmails.length > 0; // At least one requestor email required
      case 2:
        return true;
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (isStepValid(activeStep)) {
      setActiveStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  const handleSubmitForApproval = async () => {
    setIsLoading(true);
    setActiveStep(2);
    setDebugInfo(''); 

    try {
      addDebugInfo('Starting approval request submission...');
      addDebugInfo(`Request data: ${JSON.stringify(formData, null, 2)}`);

      // const baseUrl = config.getOptionalString('app_plugins.baseUrl') || '';
      const baseUrl = config.getString('backend.baseUrl');
      
      // Make the API call to submit for approval
      const apiUrl = `${baseUrl}/api/ado-project/submit-approval`;
      addDebugInfo(`Making API call to: ${apiUrl}`);
      
      const requestOptions = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      };
      
      addDebugInfo(`Request options: ${JSON.stringify(requestOptions, null, 2)}`);
      
      const response = await fetch(apiUrl, requestOptions);
      
      addDebugInfo(`Response status: ${response.status}`);
      addDebugInfo(`Response status text: ${response.statusText}`);
      addDebugInfo(`Response headers: ${JSON.stringify(Array.from(response.headers.entries()))}`);
      
      const contentType = response.headers.get('content-type');
      addDebugInfo(`Content-Type: ${contentType}`);
      
      if (!response.ok) {
        const errorText = await response.text();
        addDebugInfo(`Error response body: ${errorText}`);
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }
      
      if (!contentType || !contentType.includes('application/json')) {
        const textResponse = await response.text();
        addDebugInfo(`Non-JSON response: ${textResponse}`);
        throw new Error(`Server returned ${contentType || 'unknown content-type'} instead of JSON. Response: ${textResponse.substring(0, 200)}${textResponse.length > 200 ? '...' : ''}`);
      }

      const result: ApprovalResponse = await response.json();
      addDebugInfo(`Success response: ${JSON.stringify(result, null, 2)}`);
      
      setApprovalResult(result);
      
    } catch (error) {
      addDebugInfo(`Error caught: ${error}`);
      console.error('Full error:', error);
      
      setApprovalResult({
        success: false,
        message: `Failed to submit approval request: ${typeof error === 'object' && error !== null && 'message' in error ? (error as any).message : String(error)}`,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleStartOver = () => {
    setActiveStep(0);
    setCreationResult(null);
    setApprovalResult(null);
    setApprovalStatus(null);
    setDebugInfo('');
    setEmailTestResult(null);
    setFormData({
      organizationName: 'SWBC-FigWebDev',
      projectName: '',
      projectDescription: '',
      isDotNetProject: false,
      teamMembers: [],
      requestorEmails: [],
    });
    setNewMemberEmail('');
    setNewRequestorEmail('');
  };

  return (
    <Page themeId="tool">
      <Header 
        title="Create Azure DevOps Project" 
        subtitle="Automated project and repository setup with email approval workflow - Git, private visibility, security policies, and optional templates"
      />
      <Content>
        <Box className={classes.root}>
          {/* Test Backend Connection */}
          <Box className={classes.testConnectionSection}>
            <Typography variant="h6" gutterBottom>
              🔧 System Connection Tests
            </Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Test backend connection and email configuration before starting project creation.
            </Typography>
            <Button
              variant="outlined"
              onClick={testConnection}
              startIcon={<Settings />}
              style={{ marginRight: 16 }}
            >
              Test Backend Connection
            </Button>
            <Button
              variant="outlined"
              onClick={testEmailConnection}
              startIcon={<MailOutline />}
              style={{ marginRight: 16 }}
            >
              Test Email Configuration
            </Button>
            {debugInfo && (
              <Button
                size="small"
                onClick={() => setDebugInfo('')}
                color="secondary"
              >
                Clear Debug Log
              </Button>
            )}
            
            {/* Email Test Results */}
            {emailTestResult && (
              <Box mt={2}>
                {emailTestResult.success ? (
                  <Box className={classes.emailSuccessCard}>
                    <Box display="flex" alignItems="center" mb={1}>
                      <CheckCircleOutline style={{ color: '#2e7d32', marginRight: 8 }} />
                      <Typography variant="h6" style={{ color: '#2e7d32' }}>
                        ✅ Email Configuration Valid
                      </Typography>
                    </Box>
                    <Typography variant="body2" style={{ color: '#2e7d32' }}>
                      SMTP connection successful. Admin email: {emailTestResult.adminEmail}
                    </Typography>
                  </Box>
                ) : (
                  <Box className={classes.emailErrorCard}>
                    <Box display="flex" alignItems="center" mb={1}>
                      <ErrorOutline style={{ color: '#d32f2f', marginRight: 8 }} />
                      <Typography variant="h6" style={{ color: '#d32f2f' }}>
                        ❌ Email Configuration Issue
                      </Typography>
                    </Box>
                    <Typography variant="body2" style={{ color: '#d32f2f' }}>
                      {emailTestResult.message}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
            
            {/* Debug section */}
            {debugInfo && (
              <Box className={classes.debugSection} style={{ marginTop: 16 }}>
                <Typography variant="body2" gutterBottom>
                  <strong>Debug Information:</strong>
                </Typography>
                <Box className={classes.debugText}>
                  {debugInfo}
                </Box>
              </Box>
            )}
          </Box>

          <InfoCard title="Project Creation Wizard - Email Approval Workflow">
            <Box>
              {/* Step Indicator */}
              <Stepper activeStep={activeStep} alternativeLabel>
                {steps.map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
              
              {/* Step Content */}
              {renderStepContent(activeStep)}
              
              {/* Navigation Buttons */}
              <Box className={classes.buttonGroup}>
                {activeStep === 2 && approvalResult ? (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleStartOver}
                    startIcon={<Add />}
                  >
                    Submit Another Request
                  </Button>
                ) : (
                  <>
                    <Button
                      onClick={handleBack}
                      disabled={activeStep === 0}
                    >
                      Back
                    </Button>
                    {activeStep === steps.length - 2 ? (
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSubmitForApproval}
                        disabled={!isStepValid(activeStep) || isLoading}
                        startIcon={isLoading ? <CircularProgress size={20} /> : <Send />}
                      >
                        {isLoading ? 'Sending Email...' : 'Submit for Email Approval'}
                      </Button>
                    ) : activeStep < steps.length - 2 ? (
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleNext}
                        disabled={!isStepValid(activeStep)}
                      >
                        Next
                      </Button>
                    ) : null}
                  </>
                )}
              </Box>
            </Box>
          </InfoCard>
        </Box>
      </Content>
    </Page>
  );
};